// Mirrors the backend OrderGroupReservationStatus (#456).
//
// The API serializes it as a **camelCase string**, not a number: Bff.Api registers a
// JsonStringEnumConverter(JsonNamingPolicy.CamelCase) on its controllers, so every real C# enum
// goes out as "reserved" / "pendingReservation" / "failed" (OrderPositionStatus likewise as
// "default"). This file used to declare numeric members with a comment claiming the opposite —
// the result was that every comparison was silently false and the whole reservation branching
// never ran (#168). InvoiceType genuinely IS a number, because the BFF model declares that
// property as `int`; an enum property is the other case.
export enum ReservationStatus {
  Reserved = 'reserved',
  PendingReservation = 'pendingReservation',
  Failed = 'failed',

  // A value this build does not know — or a missing one. Deliberately its own member rather
  // than a fallback to Reserved: "reserved" is the one answer that tells the buyer their
  // purchase completed, and nothing but an explicit `reserved` may say that. Callers treat
  // Unknown the way they treat PendingReservation — announce it neutrally and look the real
  // outcome up.
  Unknown = 'unknown',
}

const BY_WIRE_NAME = new Map<string, ReservationStatus>(
  Object.values(ReservationStatus).map((value) => [value.toLowerCase(), value]),
);

// The numeric spelling of the backend enum. Not what the API sends today — this is the
// tolerance that costs three lines and covers the converter being dropped from Bff.Api, or
// another service serving the group. Getting this wrong means reporting a completed purchase
// that nobody confirmed.
const BY_ORDINAL = new Map<number, ReservationStatus>([
  [0, ReservationStatus.Reserved],
  [1, ReservationStatus.PendingReservation],
  [2, ReservationStatus.Failed],
]);

/**
 * Normalises whatever arrived on the wire into a {@link ReservationStatus}.
 *
 * `null` / `undefined` become {@link ReservationStatus.Unknown} rather than Reserved. The
 * backend property is a non-nullable enum and is always serialized, so a missing value does
 * not mean "an old order group without the saga" — it means the object itself is partial or
 * absent, and announcing a completed purchase for that is exactly the bug this replaces.
 */
export function toReservationStatus(raw: unknown): ReservationStatus {
  if (typeof raw === 'string') {
    return BY_WIRE_NAME.get(raw.toLowerCase()) ?? ReservationStatus.Unknown;
  }
  if (typeof raw === 'number') {
    return BY_ORDINAL.get(raw) ?? ReservationStatus.Unknown;
  }
  return ReservationStatus.Unknown;
}

/** True while the outcome is still open — the order view keeps polling in this state. */
export function isReservationPending(status: ReservationStatus | undefined): boolean {
  return status === ReservationStatus.PendingReservation || status === ReservationStatus.Unknown;
}

// #178: the wording for each state, in one place beside the enum — the pattern the other
// backend enums here already follow (OrderPositionStatus, InvoiceType, ProfileRole). It was the
// last enum whose words lived inline in the templates that branch on it.
//
// Reserved has none on purpose: a completed order is the ordinary case and carries no badge.
// Unknown shares PendingReservation's wording rather than getting one of its own — a value this
// build does not recognise is an open outcome as far as the buyer is concerned, and saying so is
// more use than naming our own ignorance (#168).
const LABELS: Readonly<Partial<Record<ReservationStatus, string>>> = Object.freeze({
  [ReservationStatus.PendingReservation]: $localize`:@@reservationStatus.pending:In Bearbeitung`,
  [ReservationStatus.Unknown]: $localize`:@@reservationStatus.pending:In Bearbeitung`,
  [ReservationStatus.Failed]: $localize`:@@reservationStatus.failed:Fehlgeschlagen`,
});

/** The badge text for a reservation state, or `''` where the state needs no badge. */
export function reservationStatusLabel(status: ReservationStatus | undefined): string {
  return LABELS[status] ?? '';
}

/** The Bootstrap contextual class the badge is drawn with, or `''` where there is no badge. */
export function reservationStatusBadgeClass(status: ReservationStatus | undefined): string {
  if (status === ReservationStatus.Failed) {
    return 'bg-danger';
  }
  return reservationStatusLabel(status) ? 'bg-info' : '';
}

/** The shape {@link withNormalisedReservationStatus} needs — structural, so this file does not
 *  have to import OrderGroup (which imports this one). */
interface HasReservationStatus {
  reservationStatus?: ReservationStatus;
}

/**
 * Returns a copy of the order group whose `reservationStatus` is a {@link ReservationStatus}.
 *
 * Applied by OrderService and CartService the moment a group arrives, so every consumer
 * downstream can compare against the enum and be right. Doing it per consumer instead is what
 * let #168 sit unnoticed in five places at once.
 */
export function withNormalisedReservationStatus<T extends HasReservationStatus>(orderGroup: T): T {
  if (!orderGroup) {
    return orderGroup;
  }
  return { ...orderGroup, reservationStatus: toReservationStatus(orderGroup.reservationStatus) };
}
